/**
 * AI 規定コマンド利用状況マトリクスの数値セル (工程別 / 規定コマンド計)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business マトリクスの数値セルにカーソル (またはキーボードフォーカス) を合わせると、当該セルに含まれる論理コマンド別の実行数をオーバーレイ表示する presentation component。人軸 / PJ 軸の両方から使う
 * @graph-connects sortCommandCounts [calls] 内訳の並び替えを CcCommandMatrixDetailModal の共通実装に委譲
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CcCommandMatrixCommandCount } from '../../../types/api.js';
import { sortCommandCounts } from './CcCommandMatrixDetailModal.js';

/** オーバーレイ 1 行分 (論理コマンド × 実行数)。@graph-connects none */
export interface CommandBreakdownItem {
  command: string;
  count: number;
}

/**
 * 工程 × コマンド粒度の内訳を論理コマンド粒度 (工程横断の合計) に集約する。
 * 規定コマンド計セルのオーバーレイに使う。実行数降順 → コマンド名順。
 * @graph-connects none
 */
export function aggregateCommandTotals(
  commandCounts: CcCommandMatrixCommandCount[],
): CommandBreakdownItem[] {
  const byCommand = new Map<string, number>();
  for (const item of commandCounts) {
    byCommand.set(item.command, (byCommand.get(item.command) ?? 0) + item.count);
  }
  return Array.from(byCommand, ([command, count]) => ({ command, count })).sort(
    (a, b) => b.count - a.count || a.command.localeCompare(b.command),
  );
}

/**
 * 数値セル。hover / focus でコマンド別内訳オーバーレイを出し、クリックで行の
 * 内訳モーダルを開く (hover が使えないタッチ環境・オーバーレイが見切れる環境への保険)。
 * 内訳が空 (count=0) のセルはボタン化せず、オーバーレイも持たない。
 * @graph-connects none
 */
function BreakdownCell({
  display,
  items,
  ariaLabel,
  emphasize,
  onOpenDetail,
}: {
  /** セルに表示する数値文字列 ("–" 含む) */
  display: string;
  /** オーバーレイに出す内訳。空ならオーバーレイなし */
  items: CommandBreakdownItem[];
  /** ボタンの説明 (アクセシビリティ) */
  ariaLabel: string;
  /** 規定コマンド計セルの強調表示 */
  emphasize?: boolean;
  /** 数値クリックで行の内訳モーダルを開く */
  onOpenDetail: () => void;
}) {
  const [open, setOpen] = useState(false);
  const hasItems = items.length > 0;
  const textClass = emphasize
    ? 'font-semibold text-[var(--color-text-primary)]'
    : hasItems
      ? 'text-[var(--color-text-primary)]'
      : 'text-[var(--color-text-muted)]';
  return (
    <td
      className={`relative py-2 px-3 text-right tabular-nums ${textClass}`}
      onMouseEnter={() => {
        if (hasItems) setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
    >
      {hasItems ? (
        <button
          type="button"
          onClick={() => {
            onOpenDetail();
          }}
          aria-label={ariaLabel}
          className="tabular-nums underline decoration-dotted underline-offset-2 hover:text-[var(--color-accent-primary)]"
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
          }}
        >
          {display}
        </button>
      ) : (
        display
      )}
      {open ? (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-20 min-w-[200px] rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-left shadow-lg"
        >
          <ul className="space-y-0.5">
            {items.map((item) => (
              <li key={item.command} className="flex items-center justify-between gap-4 text-xs">
                <span className="text-[var(--color-text-primary)]">{item.command}</span>
                <span className="tabular-nums text-[var(--color-text-secondary)]">
                  {item.count.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </td>
  );
}

/**
 * 工程セル列。0 は目で追いやすいよう薄い "–" にし、count > 0 のセルは
 * hover / focus で当該工程のコマンド別内訳をオーバーレイ表示、クリックで行の内訳モーダルを開く。
 * @graph-connects sortCommandCounts [calls] 工程内のコマンド並び替えを共通実装に委譲
 */
export function PhaseBreakdownCells({
  phases,
  commandCounts,
  onOpenDetail,
}: {
  phases: string[];
  commandCounts: CcCommandMatrixCommandCount[];
  /** 数値クリックで行の内訳モーダルを開く */
  onOpenDetail: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {phases.map((phase) => {
        const phaseItems = sortCommandCounts(
          commandCounts.filter((item) => item.phase === phase && item.count > 0),
          phases,
        );
        const count = phaseItems.reduce((sum, item) => sum + item.count, 0);
        return (
          <BreakdownCell
            key={phase}
            display={count > 0 ? count.toLocaleString() : '–'}
            items={phaseItems.map(({ command, count: itemCount }) => ({
              command,
              count: itemCount,
            }))}
            ariaLabel={t('ccCommandMatrix.hover.phaseAria', { phase })}
            onOpenDetail={onOpenDetail}
          />
        );
      })}
    </>
  );
}

/**
 * 規定コマンド計セル。hover / focus で工程横断のコマンド別合計をオーバーレイ表示、
 * クリックで行の内訳モーダルを開く。
 * @graph-connects none
 */
export function CommandTotalBreakdownCell({
  commandTotal,
  commandCounts,
  onOpenDetail,
}: {
  commandTotal: number;
  commandCounts: CcCommandMatrixCommandCount[];
  /** 数値クリックで行の内訳モーダルを開く */
  onOpenDetail: () => void;
}) {
  const { t } = useTranslation();
  const items = aggregateCommandTotals(commandCounts.filter((item) => item.count > 0));
  return (
    <BreakdownCell
      display={commandTotal.toLocaleString()}
      items={items}
      ariaLabel={t('ccCommandMatrix.hover.totalAria')}
      emphasize
      onOpenDetail={onOpenDetail}
    />
  );
}
